import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { handleApiError } from "../lib/handleApiError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Plus, Search, Ban, Trash2, Pencil, Network, Store, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const empty = {
  name: "", cnic: "", mobile: "", reason: "",
  severity: "high", share_to_network: false,
};

export default function Blacklist() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [severity, setSeverity] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("warn");

  const load = useCallback(async () => {
    const params = {};
    if (q) params.search = q;
    if (severity) params.severity = severity;
    try {
      const { data } = await api.get("/blacklist", { params });
      setRows(data || []);
    } catch (err) {
      handleApiError(err, "Blacklist load nahi hui");
    }
  }, [q, severity]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/auth/shop").then((r) => setMode(r.data.blacklist_enforcement_mode || "warn")).catch(() => {});
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "", cnic: row.cnic || "", mobile: row.mobile || "",
      reason: row.reason || "", severity: row.severity || "high",
      share_to_network: !!row.share_to_network,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name zaroori hai");
    if (!form.cnic.trim() && !form.mobile.trim())
      return toast.error("CNIC ya Mobile mein se ek to do");
    if ((form.reason || "").trim().length < 3) return toast.error("Reason at least 3 chars ka ho");
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/blacklist/${editingId}`, form);
        toast.success("Blacklist entry update ho gayi");
      } else {
        await api.post("/blacklist", form);
        toast.success("Blacklist mein add ho gaya");
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (err) {
      handleApiError(err, "Save nahi hua");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove "${row.name}" from blacklist?`)) return;
    try {
      await api.delete(`/blacklist/${row.id}`);
      toast.success("Hata diya gaya");
      load();
    } catch (err) {
      handleApiError(err, "Delete nahi hua");
    }
  };

  const updateMode = async (newMode) => {
    setMode(newMode);
    try {
      await api.put("/auth/shop", { blacklist_enforcement_mode: newMode });
      toast.success(`Enforcement mode set: ${newMode === "block" ? "BLOCK (strict)" : "WARN (override allowed)"}`);
    } catch (err) {
      handleApiError(err, "Mode set nahi hua");
    }
  };

  const sevTone = (s) =>
    s === "high"
      ? "bg-red-100 text-red-700 border-red-300"
      : s === "medium"
      ? "bg-amber-100 text-amber-800 border-amber-300"
      : "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className="space-y-6" data-testid="blacklist-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Ban className="w-7 h-7 text-red-600" /> Blacklist
          </h1>
          <p className="text-slate-500 mt-1">
            Khatarnak customers ki list — naye loan create karne se pehle automatically check ho jata hai.
          </p>
        </div>
        {isOwner && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button onClick={openAdd} className="bg-red-600 hover:bg-red-700" data-testid="bl-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add to Blacklist
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bl-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Blacklist Entry" : "Add to Blacklist"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Full Name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bl-name-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>CNIC</Label>
                    <Input
                      value={form.cnic}
                      onChange={(e) => setForm({ ...form, cnic: e.target.value })}
                      placeholder="42101-1234567-8"
                      data-testid="bl-cnic-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Mobile</Label>
                    <Input
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                      placeholder="03001234567"
                      data-testid="bl-mobile-input"
                    />
                  </div>
                </div>
                <div className="text-xs text-slate-500 -mt-1">
                  CNIC ya Mobile mein se kam az kam ek dena zaroori hai.
                </div>
                <div className="space-y-1.5">
                  <Label>Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger data-testid="bl-severity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Reason *</Label>
                  <Textarea
                    required
                    minLength={3}
                    rows={3}
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="Defaulted on 5 installments / IMEI fraud / etc."
                    data-testid="bl-reason-input"
                  />
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!form.share_to_network}
                    onChange={(e) => setForm({ ...form, share_to_network: e.target.checked })}
                    data-testid="bl-share-checkbox"
                  />
                  <span>
                    <span className="font-semibold">Share with HisaabPro Network</span>
                    <span className="block text-xs text-slate-500">
                      Doosri shops bhi is entry ko apne pre-check mein dekh sakein gi (anonymous).
                    </span>
                  </span>
                </label>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="bl-save-btn">
                    {saving ? "Saving…" : editingId ? "Update" : "Add"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isOwner && (
        <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="bl-mode-card">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <div className="font-display font-bold">Pre-check Enforcement</div>
              <div className="text-xs text-slate-500">
                Customer create ya loan banate waqt agar koi blacklist match ho:
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateMode("warn")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${mode === "warn" ? "bg-amber-500 border-amber-500 text-white" : "bg-slate-50 text-slate-600"}`}
              data-testid="bl-mode-warn"
            >
              Warn (Soft)
            </button>
            <button
              type="button"
              onClick={() => updateMode("block")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${mode === "block" ? "bg-red-600 border-red-600 text-white" : "bg-slate-50 text-slate-600"}`}
              data-testid="bl-mode-block"
            >
              Block (Strict)
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search name / CNIC / mobile / reason…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="bl-search-input"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {["", "low", "medium", "high"].map((s) => (
              <button
                key={s || "all"}
                type="button"
                onClick={() => setSeverity(s)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${
                  severity === s
                    ? "bg-red-600 border-red-600 text-white"
                    : "bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`bl-filter-${s || "all"}`}
              >
                {s ? s : "All"}
              </button>
            ))}
          </div>
          <div className="text-sm text-slate-500 md:ml-auto">{rows.length} entries</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">CNIC</th>
                <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                <th className="text-left px-4 py-3 font-semibold">Severity</th>
                <th className="text-left px-4 py-3 font-semibold">Reason</th>
                <th className="text-left px-4 py-3 font-semibold">Scope</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="bl-empty">
                    Abhi koi entry nahi. <span className="font-semibold text-slate-700">&quot;Add to Blacklist&quot;</span> par click karein.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bl-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 tabular text-slate-600">{r.cnic || "—"}</td>
                  <td className="px-4 py-3 tabular text-slate-600">{r.mobile || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase border ${sevTone(r.severity)}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-md truncate" title={r.reason}>{r.reason}</td>
                  <td className="px-4 py-3">
                    {r.share_to_network ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700" title="Visible across HisaabPro network">
                        <Network className="w-3.5 h-3.5" /> Network
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600" title="Sirf aap ki shop tak limited">
                        <Store className="w-3.5 h-3.5" /> Shop
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isOwner ? (
                      <div className="inline-flex gap-1">
                        <button type="button" onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" data-testid={`bl-edit-${r.id}`} title="Edit">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => remove(r)} className="p-1.5 rounded hover:bg-red-100 text-red-600" data-testid={`bl-delete-${r.id}`} title="Remove">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Owner only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
